import { useEffect, useState } from "react";
import { adminPaymentAnalytics, formatApiError } from "@/lib/api";
import StatCard from "../components/StatCard";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";

export default function PaymentsAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    adminPaymentAnalytics()
      .then((d) => { if (mounted) setData(d); })
      .catch((e) => { if (mounted) setErr(formatApiError(e)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;
  if (err) return <p className="text-red-600 text-sm">{err}</p>;
  if (!data) return null;

  const maxRev = Math.max(1, ...data.revenue_per_day.map((d) => d.revenue));

  return (
    <div className="space-y-6" data-testid="payments-analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total revenue"  value={`$${data.total_revenue.toFixed(2)}`} testId="pay-total-revenue" />
        <StatCard label="Paid orders"    value={data.total_paid_count}                testId="pay-total-count" />
        <StatCard label="Active subs"    value={data.active_subscriptions}            testId="pay-active-subs" />
        <StatCard label="Cancelled"      value={data.cancelled_subscriptions}         testId="pay-cancelled" />
      </div>

      {/* Revenue chart */}
      <AdminCard>
        <div className="p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-5">
            Revenue · last 7 days
          </p>
          <div className="flex items-end gap-3 h-48" data-testid="revenue-chart">
            {data.revenue_per_day.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end">
                <div
                  className="w-full bg-indigo-500 rounded-t-sm transition-all hover:bg-indigo-600"
                  style={{ height: `${(d.revenue / maxRev) * 100}%`, minHeight: d.revenue > 0 ? "4px" : "0" }}
                  title={`${d.date}: $${d.revenue}`}
                />
                <div className="text-[9px] font-medium text-slate-400 mt-2 uppercase">{d.date.slice(5)}</div>
                <div className="text-[10px] font-bold text-slate-700">${d.revenue.toFixed(0)}</div>
              </div>
            ))}
          </div>
        </div>
      </AdminCard>

      {/* Revenue by plan */}
      {data.by_plan.length > 0 && (
        <AdminCard header="Revenue by plan">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.by_plan.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <td className="px-6 py-3 font-semibold text-slate-900">{p.plan_id}</td>
                    <td className="px-6 py-3 text-slate-600">{p.count}</td>
                    <td className="px-6 py-3 font-bold text-indigo-600">${p.revenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AdminCard>
      )}

      {/* Recent transactions */}
      {data.recent_transactions.length > 0 && (
        <AdminCard header="Recent transactions" data-testid="recent-tx-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-tx-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {["Date", "User", "Plan", "Amount", "Status"].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recent_transactions.map((t, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <td className="px-6 py-3 text-slate-500 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-6 py-3 text-slate-700">{t.email}</td>
                    <td className="px-6 py-3 text-slate-600">{t.plan_id}</td>
                    <td className="px-6 py-3 text-slate-700">${t.amount?.toFixed(2)}</td>
                    <td className="px-6 py-3">
                      <AdminBadge variant={
                        t.payment_status === "paid" ? "success" :
                        t.payment_status === "pending" ? "warning" : "idle"
                      }>
                        {t.payment_status || "—"}
                      </AdminBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AdminCard>
      )}
    </div>
  );
}
